import React, { useEffect, useState } from "react";
import AffiliateLayout from "@/components/AffiliateLayout";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND_URL, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileText, Calendar, AlertTriangle } from "lucide-react";

function fmtMxn(n) { return `$${(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

const STATUS_LABEL = {
  draft: { l: "Borrador", c: "bg-gray-700 text-gray-300 border-gray-700" },
  sent: { l: "Enviado", c: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40" },
  paid: { l: "Pagado", c: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  canceled: { l: "Cancelado", c: "bg-red-500/20 text-red-300 border-red-500/40" },
};

export default function AffiliateStatements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/affiliate/statements").then((r) => {
      setItems(r.data.items || []); setLoading(false);
    }).catch(() => { setLoading(false); });
  }, []);

  const downloadPdf = async (statementId) => {
    try {
      const resp = await api.get(`/affiliate/statements/${statementId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al descargar");
    }
  };

  return (
    <AffiliateLayout user={user}>
      <div>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-brand-orange-500 font-semibold">Fase 14.4</p>
          <h1 className="mt-2 text-3xl font-bold text-white font-display flex items-center gap-2">
            <FileText className="w-7 h-7" /> Statements
          </h1>
          <p className="text-sm text-gray-400 mt-1">Estados de cuenta mensuales con detalle de comisiones</p>
        </div>
        <div className="p-3 mb-4 rounded-lg bg-brand-orange-500/10 border border-brand-orange-500/40 flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-brand-orange-500 flex-shrink-0" />
          <span className="text-gray-300">
            <span className="text-brand-orange-500 font-semibold">MOCKED</span> · los payouts marcados como "Pagado" son simulados; no hay transferencia real en este ambiente demo.
          </span>
        </div>
        <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-x-auto" data-testid="affiliate-statements-table">
          {loading ? (
            <div className="p-8 text-center text-gray-500 text-sm">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">Sin statements aún. Se generan mensualmente por el admin.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-[#0B0F14] border-b border-gray-800">
                <tr>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Periodo</th>
                  <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest"># Comisiones</th>
                  <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Gross</th>
                  <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Net</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Estado</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Referencia</th>
                  <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {items.map((s) => (
                  <tr key={s.id} data-testid={`statement-row-${s.id}`} className="hover:bg-[#0B0F14]">
                    <td className="px-4 py-3">
                      <div className="text-sm text-white font-semibold flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-gray-500" />
                        {(s.period_start || "").slice(0, 7)}
                      </div>
                      <div className="text-[10px] text-gray-500 font-mono">{s.id.slice(0, 8)}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-mono text-white">{s.commissions_count}</td>
                    <td className="px-4 py-3 text-right text-sm font-mono text-gray-300">{fmtMxn(s.gross_mxn)}</td>
                    <td className="px-4 py-3 text-right text-sm font-mono text-brand-orange-500 font-bold">{fmtMxn(s.net_mxn)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-[10px] rounded border font-semibold uppercase tracking-widest ${(STATUS_LABEL[s.status] || {}).c}`}>
                        {(STATUS_LABEL[s.status] || {}).l || s.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-400">{s.payment_reference || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => downloadPdf(s.id)}
                        data-testid={`statement-pdf-${s.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-brand-orange-500 text-brand-orange-500 hover:bg-brand-orange-500/10 font-semibold">
                        <Download className="w-3 h-3" /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AffiliateLayout>
  );
}
